import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  TextField,
  InputAdornment,
  Chip,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Snackbar,
  Divider,
  Tooltip,
} from '@mui/material';
import {
  Search as SearchIcon,
  Visibility as ViewIcon,
  CardMembership as GrantIcon,
  Clear as ClearIcon,
  FilterListOff as ResetFilterIcon,
  PeopleAlt as PeopleIcon,
  Paid as PaidIcon,
  AdminPanelSettings as AdminIcon,
  Quiz as QuizIcon,
  PersonOff as FreeIcon,
  WorkspacePremium as ActiveIcon,
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import api from './api';

// Only the statuses that actually occur in the data (users table ∪ subscriptions).
const SUBSCRIPTION_STATUSES = ['ACTIVE', 'FREE', 'EXPIRED', 'CANCELLED', 'INACTIVE'];

// Acquisition segment — the meaningful "how did this user get access" filter.
const SEGMENT_FILTERS = [
  { value: 'paid',  label: 'Paying (Purchased)' },
  { value: 'admin', label: 'Admin Granted' },
  { value: 'quiz',  label: 'Quiz Grant' },
  { value: 'free',  label: 'Free / No Plan' },
];

const segmentChip = {
  paid:  { label: 'Paying', color: 'success' },
  admin: { label: 'Admin Grant', color: 'secondary' },
  quiz:  { label: 'Quiz Grant', color: 'info' },
  free:  { label: 'Free', color: 'default' },
};

const PLAN_FILTERS = [
  { value: 'web',   label: 'Web Plan' },
  { value: 'mobile', label: 'Mobile Plan' },
  { value: 'combi',  label: 'Combi Plan' },
];

const statusColors = {
  ACTIVE: 'success',
  FREE: 'default',
  TRIAL: 'info',
  CANCELLED: 'error',
  EXPIRED: 'error',
  PAUSED: 'warning',
  PAST_DUE: 'warning',
  PENDING: 'warning',
  INACTIVE: 'default',
};

function formatDate(dateStr) {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('en-GB', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
  });
}

function formatCurrency(amount, currency) {
  if (amount === null || amount === undefined) return '-';
  return `${Number(amount).toFixed(2)} ${currency || ''}`;
}

/**
 * Derive the effective subscription status and type for display.
 * Prefer the latest UserSubscription row over the stale users-table fields,
 * because older manual grants may not have synced the users table.
 * If no subscriptions exist at all → "No Plan".
 */
function getEffectiveSub(user) {
  const latest = user.subscriptions?.[0];
  if (!latest) {
    return { status: null, type: null, planName: null, noPlan: true };
  }
  return {
    status: latest.status,
    type: latest.subscriptionType,
    planName: latest.planName || null,
    noPlan: false,
  };
}

// ── User Detail Dialog ────────────────────────────────────────────────────────

function UserDetailDialog({ open, onClose, user, onGrant }) {
  if (!user) return null;
  const eff = getEffectiveSub(user);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span>{user.firstName} {user.lastName}</span>
        <Button
          variant="contained"
          size="small"
          startIcon={<GrantIcon />}
          onClick={() => { onClose(); onGrant(user); }}
        >
          Grant Subscription
        </Button>
      </DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[
            ['Email', user.email],
            ['Country', user.country || '-'],
            ['City', user.city || '-'],
            ['Date of Birth', formatDate(user.dateOfBirth)],
            ['Diabetes Type', user.diabetesType || '-'],
            ['Therapy', user.currentTherapy || '-'],
            ['Language', user.preferredLanguage || '-'],
            ['Timezone', user.timezone || '-'],
            ['Social Tariff', user.isSocialTariffApproved ? 'Approved' : 'No'],
            ['Stripe Customer ID', user.stripeCustomerId || '-'],
            ['Joined', formatDate(user.createdAt)],
          ].map(([label, value]) => (
            <Grid item xs={12} sm={6} key={label}>
              <Typography variant="caption" color="text.secondary">{label}</Typography>
              <Typography variant="body2" fontWeight="medium" sx={{ wordBreak: 'break-all' }}>{value}</Typography>
            </Grid>
          ))}
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">Effective Status</Typography>
            <Box mt={0.5}>
              {eff.noPlan
                ? <Chip label="No Plan" size="small" variant="outlined" />
                : <Chip label={eff.status} color={statusColors[eff.status] || 'default'} size="small" />
              }
            </Box>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">Effective Type</Typography>
            <Typography variant="body2">{eff.noPlan ? 'No Plan' : eff.type}</Typography>
          </Grid>
        </Grid>

        {user.isAdminAssigned && (
          <>
            <Divider sx={{ my: 2 }} />
            <Box sx={{ backgroundColor: 'secondary.50', border: '1px solid', borderColor: 'secondary.200', borderRadius: 2, p: 2 }}>
              <Typography variant="subtitle2" fontWeight="bold" color="secondary.dark" gutterBottom>
                Admin Assignment
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">Assigned Plan</Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {user.subscriptions?.[0]?.planName || '-'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">Assigned On</Typography>
                  <Typography variant="body2" fontWeight="medium">{formatDate(user.adminAssignedAt)}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">Assigned By (Admin ID)</Typography>
                  <Typography variant="body2" fontWeight="medium" sx={{ wordBreak: 'break-all', fontFamily: 'monospace', fontSize: '0.78rem' }}>
                    {user.adminAssignedBy || '-'}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          </>
        )}

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
          Subscription History
        </Typography>

        {!user.subscriptions || user.subscriptions.length === 0 ? (
          <Typography variant="body2" color="text.secondary">No subscriptions found.</Typography>
        ) : (
          <TableContainer component={Paper} variant="outlined" sx={{ mt: 1 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell>Plan</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell>Interval</TableCell>
                  <TableCell>Start</TableCell>
                  <TableCell>End</TableCell>
                  <TableCell>Source</TableCell>
                  <TableCell>Created</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {user.subscriptions.map((sub) => (
                  <TableRow key={sub.id} hover>
                    <TableCell>{sub.planName || '-'}</TableCell>
                    <TableCell>{sub.subscriptionType}</TableCell>
                    <TableCell>
                      <Chip label={sub.status} color={statusColors[sub.status] || 'default'} size="small" />
                    </TableCell>
                    <TableCell>{formatCurrency(sub.amount, sub.currency)}</TableCell>
                    <TableCell>{sub.billingInterval || '-'}</TableCell>
                    <TableCell>{formatDate(sub.startDate)}</TableCell>
                    <TableCell>{formatDate(sub.endDate)}</TableCell>
                    <TableCell>
                      <Chip
                        label={sub.paymentSubscriptionId === 'manual_entry' ? 'Manual' : 'Payment'}
                        size="small"
                        variant="outlined"
                        color={sub.paymentSubscriptionId === 'manual_entry' ? 'secondary' : 'primary'}
                      />
                    </TableCell>
                    <TableCell>{formatDate(sub.createdAt)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Grant Subscription Dialog ─────────────────────────────────────────────────

function toDateInput(dateStr) {
  if (!dateStr) return '';
  return new Date(dateStr).toISOString().split('T')[0];
}

function defaultEndDate(fromDateStr) {
  const d = fromDateStr ? new Date(fromDateStr) : new Date();
  d.setMonth(d.getMonth() + 6);
  return d.toISOString().split('T')[0];
}

function getSubscriptionDuration(start, end) {
  if (!start || !end) return null;
  const s = new Date(start);
  const e = new Date(end);
  if (e <= s) return null;

  let years = e.getFullYear() - s.getFullYear();
  let months = e.getMonth() - s.getMonth();
  let days = e.getDate() - s.getDate();

  if (days < 0) {
    months -= 1;
    days += new Date(e.getFullYear(), e.getMonth(), 0).getDate();
  }
  if (months < 0) {
    years -= 1;
    months += 12;
  }

  const parts = [];
  if (years > 0) parts.push(years + (years === 1 ? ' year' : ' years'));
  if (months > 0) parts.push(months + (months === 1 ? ' month' : ' months'));
  if (years === 0 && days > 0) parts.push(days + (days === 1 ? ' day' : ' days'));
  return parts.join(' ');
}

function GrantSubscriptionDialog({ open, onClose, user, onGranted }) {
  const today = new Date().toISOString().split('T')[0];
  const tomorrow = (() => { const d = new Date(); d.setDate(d.getDate() + 1); return d.toISOString().split('T')[0]; })();

  const [form, setForm] = useState({ startDate: today, endDate: defaultEndDate(today) });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setError('');
      const latestSub = user?.subscriptions?.[0];
      const start = latestSub?.startDate ? toDateInput(latestSub.startDate) : today;
      // For updates: keep existing end date only if it's in the future, else default
      const existingEnd = latestSub?.endDate ? toDateInput(latestSub.endDate) : null;
      const end = (existingEnd && existingEnd > today) ? existingEnd : defaultEndDate(start > today ? start : today);
      setForm({ startDate: start, endDate: end });
    }
  }, [open, user?.id]); // eslint-disable-line

  const handleChange = (field) => (e) => {
    setForm((prev) => {
      const updated = { ...prev, [field]: e.target.value };
      if (field === 'startDate' && updated.endDate && updated.endDate <= e.target.value) {
        updated.endDate = defaultEndDate(e.target.value);
      }
      return updated;
    });
    setError('');
  };

  const handleSubmit = async () => {
    if (!form.startDate || !form.endDate) {
      setError('Start Date and End Date are required.');
      return;
    }
    if (form.endDate <= form.startDate) {
      setError('End Date must be after Start Date.');
      return;
    }
    if (form.endDate <= today) {
      setError('End Date must be a future date.');
      return;
    }
    setError('');
    setSubmitting(true);
    try {
      await api.grantAdminUserSubscription(user.id, { startDate: form.startDate, endDate: form.endDate });
      onGranted(hasExisting);
      onClose();
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to grant subscription. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!user) return null;

  const hasExisting = !!user.subscriptions?.[0];
  const duration = getSubscriptionDuration(form.startDate, form.endDate);
  const endDateError = form.startDate && form.endDate && (form.endDate <= form.startDate || form.endDate <= today);
  const endHelperText = !form.endDate
    ? ' '
    : form.endDate <= today
      ? 'End Date must be a future date.'
      : form.endDate <= form.startDate
        ? 'End Date must be after Start Date.'
        : ' ';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>
        {hasExisting ? 'Update' : 'Grant'} Subscription for {user.firstName} {user.lastName}
      </DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {hasExisting
            ? 'Update the existing Combi Package (Premium, EUR 16.90/month) subscription period.'
            : 'This will grant the Combi Package (Premium, EUR 16.90/month) for the selected period.'
          }
        </Typography>

        <Grid container spacing={2} alignItems="flex-start">
          <Grid item xs={6}>
            <TextField
              fullWidth required
              label="Start Date"
              type="date"
              value={form.startDate}
              onChange={handleChange('startDate')}
              slotProps={{
                inputLabel: { shrink: true },
                htmlInput: { min: !hasExisting ? today : undefined },
              }}
              helperText=" "
            />
          </Grid>

          <Grid item xs={6}>
            <TextField
              fullWidth required
              label="End Date"
              type="date"
              value={form.endDate}
              onChange={handleChange('endDate')}
              slotProps={{
                inputLabel: { shrink: true },
                htmlInput: { min: tomorrow },
              }}
              error={!!endDateError}
              helperText={endHelperText}
            />
          </Grid>

          <Grid item xs={12} sx={{ mt: -1 }}>
            <Typography
              variant="body2"
              sx={{ fontStyle: 'italic', color: duration && !endDateError ? 'text.secondary' : 'transparent', minHeight: '1.4em' }}
            >
              Duration: <strong>{duration || ' '}</strong>
            </Typography>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={submitting || !!endDateError}
          startIcon={submitting ? <CircularProgress size={16} /> : <GrantIcon />}
        >
          {submitting ? (hasExisting ? 'Updating…' : 'Granting…') : (hasExisting ? 'Update Subscription' : 'Grant Subscription')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Analytics Stat Card ───────────────────────────────────────────────────────

// `color` is an MUI palette key ('primary' | 'secondary' | 'success' | 'info' | 'warning' | 'error')
// or 'neutral' for a grey card. Tints are derived with alpha() so no numeric palette shades are needed.
function resolveMain(theme, color) {
  if (color === 'neutral') return theme.palette.grey[600];
  return theme.palette[color]?.main || theme.palette.primary.main;
}

function StatCard({ icon, label, value, subtitle, color = 'primary', loading }) {
  return (
    <Paper sx={{ p: 2, height: '100%', display: 'flex', alignItems: 'center', gap: 1.5 }}>
      <Box
        sx={(theme) => {
          const main = resolveMain(theme, color);
          return {
            width: 44, height: 44, borderRadius: 2, flexShrink: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            bgcolor: alpha(main, 0.12),
            color: main,
          };
        }}
      >
        {icon}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.2 }}>
          {label}
        </Typography>
        {loading ? (
          <CircularProgress size={18} sx={{ mt: 0.5 }} />
        ) : (
          <Typography
            variant="h6"
            fontWeight="bold"
            sx={(theme) => ({ lineHeight: 1.2, color: resolveMain(theme, color) })}
          >
            {value}
          </Typography>
        )}
        {subtitle && !loading && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.2 }}>
            {subtitle}
          </Typography>
        )}
      </Box>
    </Paper>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────

export default function UserManagementManager() {
  const [users, setUsers] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, page: 0, limit: 20, totalPages: 0 });
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [planFilter, setPlanFilter] = useState('');
  const [segmentFilter, setSegmentFilter] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [sortDir, setSortDir] = useState('desc');

  const [detailUser, setDetailUser] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);

  const [grantUser, setGrantUser] = useState(null);
  const [grantOpen, setGrantOpen] = useState(false);

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);

  const searchTimer = useRef(null);
  const hasFilters = search || statusFilter || planFilter || segmentFilter;

  const loadStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const result = await api.getAdminUserStats();
      setStats(result.data || null);
    } catch {
      // Non-fatal: the table still works without the analytics cards.
    } finally {
      setStatsLoading(false);
    }
  }, []);

  useEffect(() => { loadStats(); }, [loadStats]);

  const loadUsers = useCallback(async (overrides = {}) => {
    setLoading(true);
    try {
      const params = {
        search: overrides.search !== undefined ? overrides.search : search,
        subscriptionStatus: overrides.statusFilter !== undefined ? overrides.statusFilter : statusFilter,
        planType: overrides.planFilter !== undefined ? overrides.planFilter : planFilter,
        segment: overrides.segmentFilter !== undefined ? overrides.segmentFilter : segmentFilter,
        sortDir: overrides.sortDir !== undefined ? overrides.sortDir : sortDir,
        page: overrides.page !== undefined ? overrides.page : page,
        limit: overrides.rowsPerPage !== undefined ? overrides.rowsPerPage : rowsPerPage,
      };
      // Strip empty strings so they don't appear as query params
      Object.keys(params).forEach((k) => { if (params[k] === '') delete params[k]; });
      const result = await api.getAdminUsers(params);
      setUsers(result.data || []);
      setPagination(result.pagination || {});
    } catch {
      setSnackbar({ open: true, message: 'Failed to load users.', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter, planFilter, segmentFilter, sortDir, page, rowsPerPage]);

  useEffect(() => {
    loadUsers();
  }, [page, rowsPerPage, statusFilter, planFilter, segmentFilter, sortDir]); // eslint-disable-line

  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearch(val);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      setPage(0);
      loadUsers({ search: val, page: 0 });
    }, 400);
  };

  const handleStatusFilter = (e) => { setStatusFilter(e.target.value); setPage(0); };
  const handlePlanFilter = (e) => { setPlanFilter(e.target.value); setPage(0); };
  const handleSegmentFilter = (e) => { setSegmentFilter(e.target.value); setPage(0); };

  const handleReset = () => {
    setSearch(''); setStatusFilter(''); setPlanFilter(''); setSegmentFilter(''); setPage(0);
    loadUsers({ search: '', statusFilter: '', planFilter: '', segmentFilter: '', page: 0 });
  };

  const handleJoinedSort = () => {
    const next = sortDir === 'desc' ? 'asc' : 'desc';
    setSortDir(next);
    setPage(0);
    loadUsers({ sortDir: next, page: 0 });
  };

  const handlePageChange = (_, newPage) => setPage(newPage);
  const handleRowsPerPageChange = (e) => { setRowsPerPage(Number(e.target.value)); setPage(0); };

  const handleViewUser = async (user) => {
    setDetailOpen(true);
    setDetailLoading(true);
    try {
      const result = await api.getAdminUser(user.id);
      setDetailUser(result.data);
    } catch {
      setSnackbar({ open: true, message: 'Failed to load user details.', severity: 'error' });
      setDetailOpen(false);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleOpenGrant = (user) => {
    setGrantUser(user);
    setGrantOpen(true);
  };

  const handleGranted = (updated) => {
    setSnackbar({ open: true, message: updated ? 'Subscription updated successfully!' : 'Subscription granted successfully!', severity: 'success' });
    loadUsers();
    loadStats(); // a new/updated grant shifts the segment & active counts
  };

  return (
    <Box>
      <Typography variant="h5" fontWeight="bold" gutterBottom>
        User Management
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        View all registered users, their subscription details, and grant manual subscriptions.
      </Typography>

      {/* Analytics cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={4} md={2}>
          <StatCard
            icon={<PeopleIcon />}
            color="primary"
            label="Total Users"
            value={stats?.totalUsers ?? 0}
            loading={statsLoading}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <StatCard
            icon={<ActiveIcon />}
            color="info"
            label="Active Subscriptions"
            value={stats?.activeSubscriptions?.total ?? 0}
            subtitle={stats
              ? `${stats.activeSubscriptions.paid} paid · ${stats.activeSubscriptions.admin} admin · ${stats.activeSubscriptions.quiz} quiz`
              : null}
            loading={statsLoading}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <StatCard
            icon={<PaidIcon />}
            color="success"
            label="Active Paying Customers"
            value={stats?.revenue?.payingCustomers ?? 0}
            subtitle={stats ? `€${stats.revenue.total.toFixed(2)} revenue · €${stats.revenue.mrr.toFixed(2)} MRR` : null}
            loading={statsLoading}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <StatCard
            icon={<AdminIcon />}
            color="secondary"
            label="Admin Granted"
            value={stats?.segments?.admin ?? 0}
            subtitle={stats ? `${stats.grants.adminGrantsTotal} total grants` : null}
            loading={statsLoading}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <StatCard
            icon={<QuizIcon />}
            color="info"
            label="Quiz Grants"
            value={stats?.segments?.quiz ?? 0}
            subtitle={stats ? `${stats.grants.quizGrantsTotal} total grants` : null}
            loading={statsLoading}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <StatCard
            icon={<FreeIcon />}
            color="neutral"
            label="Free / No Plan"
            value={stats?.segments?.free ?? 0}
            subtitle={stats ? `${stats.socialTariffApproved} social tariff` : null}
            loading={statsLoading}
          />
        </Grid>
      </Grid>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          {/* Search */}
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by name or email…"
              value={search}
              onChange={handleSearchChange}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                  endAdornment: search ? (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => { setSearch(''); loadUsers({ search: '', page: 0 }); }}>
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                },
              }}
            />
          </Grid>

          {/* Segment filter — the primary "how did they get access" lens */}
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Segment</InputLabel>
              <Select
                value={segmentFilter}
                label="Segment"
                onChange={handleSegmentFilter}
                sx={{
                  borderRadius: 2,
                  minWidth: '200px',
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'primary.main' },
                }}
              >
                <MenuItem value="">All Segments</MenuItem>
                {SEGMENT_FILTERS.map((s) => (
                  <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Status filter */}
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Subscription Status</InputLabel>
              <Select
                value={statusFilter}
                label="Subscription Status"
                onChange={handleStatusFilter}
                sx={{
                  borderRadius: 2,
                  minWidth: '200px',
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'primary.main' },
                }}
              >
                <MenuItem value="">All Statuses</MenuItem>
                {SUBSCRIPTION_STATUSES.map((s) => (
                  <MenuItem key={s} value={s}>{s}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Plan filter */}
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Plan Type</InputLabel>
              <Select
                value={planFilter}
                label="Plan Type"
                onChange={handlePlanFilter}
                sx={{
                  borderRadius: 2,
                  minWidth: '200px',
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'primary.main' },
                }}
              >
                <MenuItem value="">All Plans</MenuItem>
                {PLAN_FILTERS.map((p) => (
                  <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Reset filters button, shown only when a filter is active */}
          {hasFilters && (
            <Grid item xs={12} sm={12} md={4}>
              <Button
                variant="outlined"
                startIcon={<ResetFilterIcon />}
                onClick={handleReset}
              >
                Reset Filters
              </Button>
            </Grid>
          )}
        </Grid>
      </Paper>

      {/* Total count */}
      <Box sx={{ mb: 1, display: 'flex', justifyContent: 'flex-end' }}>
        <Typography variant="caption" color="text.secondary">
          {pagination.total ?? 0} user{pagination.total !== 1 ? 's' : ''} found
        </Typography>
      </Box>

      {/* Users Table */}
      <Paper>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.100' }}>
                <TableCell sx={{ fontWeight: 'bold' }}>Name</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Email</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Segment</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Plan</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Sub Start</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Sub End</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Assigned By</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>
                  <TableSortLabel
                    active
                    direction={sortDir}
                    onClick={handleJoinedSort}
                  >
                    Joined
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold' }} align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={32} />
                  </TableCell>
                </TableRow>
              ) : users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">No users found.</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                users.map((user) => {
                  const eff = getEffectiveSub(user);
                  const latestSub = user.subscriptions?.[0];
                  return (
                    <TableRow key={user.id} hover>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        <Tooltip title={`${user.firstName} ${user.lastName}`} placement="top">
                          <span>{user.firstName} {user.lastName}</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        <Tooltip title={user.email} placement="top">
                          <span>{user.email}</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        {(() => {
                          const seg = segmentChip[user.segment] || segmentChip.free;
                          return <Chip label={seg.label} color={seg.color} size="small" variant="outlined" />;
                        })()}
                      </TableCell>
                      <TableCell>
                        {eff.noPlan
                          ? <Chip label="No Plan" size="small" variant="outlined" sx={{ color: 'text.secondary', borderColor: 'divider' }} />
                          : <Chip label={eff.status} color={statusColors[eff.status] || 'default'} size="small" />
                        }
                      </TableCell>
                      <TableCell>{latestSub?.planName || '-'}</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {latestSub ? formatDate(latestSub.startDate) : '-'}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {latestSub ? formatDate(latestSub.endDate) : '-'}
                      </TableCell>
                      <TableCell>
                        {user.isAdminAssigned ? (
                          <Tooltip title={`Admin ID: ${user.adminAssignedBy || 'unknown'} · On: ${formatDate(user.adminAssignedAt)}`} placement="top">
                            <Box>
                              <Chip label="Manual" size="small" color="secondary" variant="outlined" sx={{ mb: 0.5 }} />
                              <Typography
                                variant="caption"
                                display="block"
                                color="text.secondary"
                                sx={{ maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                              >
                                {user.adminAssignedBy || '-'}
                              </Typography>
                            </Box>
                          </Tooltip>
                        ) : '-'}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDate(user.createdAt)}</TableCell>
                      <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                        <Tooltip title="View details">
                          <IconButton size="small" color="primary" onClick={() => handleViewUser(user)}>
                            <ViewIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Grant subscription">
                          <IconButton size="small" color="success" onClick={() => handleOpenGrant(user)}>
                            <GrantIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 20, 50, 100]}
          component="div"
          count={pagination.total || 0}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
        />
      </Paper>

      {/* User Detail Dialog */}
      <Dialog open={detailOpen && detailLoading} maxWidth="sm">
        <DialogContent sx={{ py: 6, px: 8, textAlign: 'center' }}>
          <CircularProgress />
          <Typography sx={{ mt: 2 }}>Loading…</Typography>
        </DialogContent>
      </Dialog>

      {!detailLoading && detailUser && (
        <UserDetailDialog
          open={detailOpen}
          onClose={() => { setDetailOpen(false); setDetailUser(null); }}
          user={detailUser}
          onGrant={(u) => { handleOpenGrant(u); }}
        />
      )}

      {/* Grant Subscription Dialog */}
      <GrantSubscriptionDialog
        open={grantOpen}
        onClose={() => { setGrantOpen(false); setGrantUser(null); }}
        user={grantUser}
        onGranted={handleGranted}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
